// =====================================================================
// StockMovementDialog (Financeiro F2-ext / S59) — registra movimento de estoque.
// entrada (+) | saida (−, bloqueia saldo<0) | ajuste (DEFINE o saldo contado).
// =====================================================================
import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Box, Typography, CircularProgress, Checkbox, FormControlLabel
} from "@material-ui/core";
import { toast } from "react-toastify";
import {
  addStockMovement, financeErrorMessage, STOCK_MOVE_TYPES, fmtMoney, listStockSuppliers
} from "../../services/financeService";
import useIsMobile from "../../utils/useIsMobile";

const num = (v) => Number(String(v).replace(",", ".")) || 0;

const StockMovementDialog = ({ open, product, onClose, onSaved }) => {
  const isMobile = useIsMobile();
  const [type, setType] = useState("entrada");
  const [qty, setQty] = useState("");
  const [lot, setLot] = useState("");
  const [expiry, setExpiry] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  // Fase 2 — compra (entrada): fornecedor + NF + gerar conta a pagar
  const [supplierId, setSupplierId] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [createExpense, setCreateExpense] = useState(false);
  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => {
    if (open) {
      setType("entrada"); setQty(""); setLot(""); setExpiry(""); setUnitCost(""); setReason("");
      setSupplierId(""); setInvoiceNumber(""); setCreateExpense(false);
      listStockSuppliers("true").then((r) => setSuppliers((r && r.suppliers) || [])).catch(() => setSuppliers([]));
    }
  }, [open]);

  // some o "gerar conta a pagar" se o fornecedor/custo for limpo
  useEffect(() => {
    if (!supplierId || unitCost === "") setCreateExpense(false);
  }, [supplierId, unitCost]);

  const qtyLabel = type === "ajuste" ? "Quantidade contada (novo saldo)" : "Quantidade";

  const handleSave = async () => {
    const q = num(qty);
    if (type === "ajuste" ? q < 0 : q <= 0) { toast.error("Informe uma quantidade válida."); return; }
    setSaving(true);
    try {
      await addStockMovement(product.id, {
        type, qty: q,
        lot: lot || undefined,
        expiry: expiry || undefined,
        unitCost: unitCost !== "" ? num(unitCost) : undefined,
        reason: reason || undefined,
        supplierId: type === "entrada" && supplierId ? supplierId : undefined,
        invoiceNumber: type === "entrada" && invoiceNumber ? invoiceNumber : undefined,
        createExpense: type === "entrada" && createExpense ? true : undefined
      });
      toast.success("Movimento registrado.");
      onSaved && onSaved();
    } catch (err) {
      toast.error(financeErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth fullScreen={isMobile}>
      <DialogTitle>Movimentar estoque</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {product?.name} — saldo atual: <strong>{Number(product?.currentQty || 0)} {product?.unit || ""}</strong>
        </Typography>
        <TextField select label="Tipo" value={type} onChange={(e) => setType(e.target.value)} fullWidth margin="dense">
          {STOCK_MOVE_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
        </TextField>
        <TextField label={qtyLabel} type="number" value={qty} onChange={(e) => setQty(e.target.value)}
          fullWidth margin="dense" inputProps={{ step: "0.01", min: "0" }} autoFocus />
        {type === "entrada" && (
          <>
            <Box display="flex" style={{ gap: 12, flexWrap: "wrap" }}>
              <TextField label="Lote" value={lot} onChange={(e) => setLot(e.target.value)} margin="dense" style={{ flexGrow: 1, minWidth: 130 }} />
              <TextField label="Validade" type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)}
                margin="dense" InputLabelProps={{ shrink: true }} style={{ flexGrow: 1, minWidth: 150 }} />
            </Box>
            <TextField label="Custo unitário (R$)" type="number" value={unitCost} onChange={(e) => setUnitCost(e.target.value)}
              fullWidth margin="dense" inputProps={{ step: "0.01", min: "0" }}
              helperText={unitCost !== "" && qty !== "" ? `Total da entrada: ${fmtMoney(num(unitCost) * num(qty))}` : " "} />
            <TextField select label="Fornecedor (opcional)" value={supplierId}
              onChange={(e) => setSupplierId(e.target.value)} fullWidth margin="dense">
              <MenuItem value="">— nenhum —</MenuItem>
              {suppliers.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
            </TextField>
            <TextField label="Nota fiscal (NF)" value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)} fullWidth margin="dense" />
            <FormControlLabel
              control={<Checkbox checked={createExpense} color="primary"
                onChange={(e) => setCreateExpense(e.target.checked)}
                disabled={!supplierId || unitCost === ""} />}
              label="Gerar conta a pagar (Tesouraria)"
            />
            {createExpense && (
              <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                Cria uma conta a pagar de {fmtMoney(num(unitCost) * num(qty))} pro fornecedor.
              </Typography>
            )}
          </>
        )}
        <TextField label="Motivo / observação" value={reason} onChange={(e) => setReason(e.target.value)}
          fullWidth margin="dense" placeholder={type === "saida" ? "ex.: aplicação na paciente Fulana" : ""} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}>Registrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StockMovementDialog;
